import { message } from 'antd';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Commodity } from '@/components/RightProvider/const';
import { RightContext } from '@/components/RightProvider/context';
import { UseTranslateType } from '@/hooks/useTranslate';
import PaymentStore, {
  CurrencyType,
  filterCommoditiesByCurrency,
  getAvailableCurrencies,
  OrderInfo,
  OrderType,
  PaymentMethod,
} from '@/store/Payment';
import { executePayment, executePolling } from '@/store/Payment/paymentStrategy';
import UserStore, { IDENTITYKEY } from '@/store/User';
import { AIFunction } from '@/utils/const';
import { EventNewID, GoogleEventID, StatusEnum } from '@/utils/eventlog/eventNewConfig';
import { trackEvent, trackGoogleEvent } from '@/utils/eventlog/logAdapter';

import { getPlanTag } from '../utils';

interface UsePaymentProps {
  t: UseTranslateType | undefined;
}

interface HandlePayProps {
  data: Commodity[number];
  feature: AIFunction;
  btnIndex: number;
  price: string;
  currency: string;
}

// 获取推荐的积分包
function getRecommendCredit(singlePurchaseConfig: Commodity) {
  try {
    return singlePurchaseConfig.find((item) => item?.content?.label);
  } catch (error) {
    return false;
  }
}

const useAICreditPayment = ({ t }: UsePaymentProps) => {
  // 从 Store 获取状态
  const { paymentMethod, selectedCurrency, setSelectedCurrency } = PaymentStore();

  // 本地状态
  const [isPurchaseSuccess, setIsPurchaseSuccess] = useState(false);
  const [highlightSingleCode, setHighlightSingleCode] = useState<string>('');

  // 从 Context 获取配置
  const { singlePurchaseConfig, withUpdate } = useContext(RightContext);
  const purchaseLimitLoading = UserStore((store) => store.purchaseLimitLoading);

  // 格式化数据
  const formatSingleData = useMemo(() => {
    return [...singlePurchaseConfig].sort((a, b) => a.sort - b.sort);
  }, [singlePurchaseConfig]);

  // 加载状态
  const [singleLoadingStates, setSingleLoadingStates] = useState<boolean[]>([]);

  // 埋点用 ref
  const featureRef = useRef<AIFunction>(AIFunction.Other);
  const activeTabRef = useRef<'once'>('once');
  const activeComboRef = useRef<IDENTITYKEY>('advancedVip');

  // 轮询定时器
  const timer = useRef<NodeJS.Timeout | null>(null);
  const pollPayKey = 'pollPayKey';

  // 订单信息
  const orderInfo = useRef<OrderInfo>({
    orderId: '',
    orderType: OrderType.Once,
    orderCommodityCode: '',
  });

  // 获取可用的货币类型
  const availableCurrencies = useMemo(() => {
    return getAvailableCurrencies(formatSingleData as any[]);
  }, [formatSingleData]);

  const filteredSingleData = useMemo(() => {
    const filtered = filterCommoditiesByCurrency(
      formatSingleData as any[],
      selectedCurrency,
    );
    return filtered.length ? filtered : formatSingleData;
  }, [formatSingleData, selectedCurrency]);

  // 初始化加载状态
  useEffect(() => {
    setSingleLoadingStates(new Array(filteredSingleData.length).fill(false));
  }, [filteredSingleData.length]);

  // 获取高亮的积分包
  useEffect(() => {
    const aicredit = getRecommendCredit(singlePurchaseConfig);
    if (aicredit) {
      setHighlightSingleCode(aicredit.commodityCode);
    }
  }, [singlePurchaseConfig]);

  useEffect(() => {
    if (!filteredSingleData.length) return;
    const exists = filteredSingleData.some(
      (item) => item.commodityCode === highlightSingleCode,
    );
    if (!exists) {
      setHighlightSingleCode(filteredSingleData[0].commodityCode);
    }
  }, [filteredSingleData, highlightSingleCode]);

  // 清理
  useEffect(() => {
    return () => {
      timer.current && clearTimeout(timer.current);
    };
  }, []);

  // 设置按钮加载状态
  const setSingleBtnLoading = useCallback((index: number, loading: boolean) => {
    setSingleLoadingStates((prev) => {
      const newStates = [...prev];
      newStates[index] = loading;
      return newStates;
    });
  }, []);

  // 支付成功回调
  const handlePaymentSuccess = useCallback(async () => {
    setIsPurchaseSuccess(true);

    trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
      source: featureRef.current,
      plan: getPlanTag(activeComboRef.current, activeTabRef.current),
      result: StatusEnum.SUCCESS,
    });

    message.open({
      key: pollPayKey,
      type: 'success',
      content: t && t('merror.sub.purchasesuc'),
      duration: 2,
    });

    await withUpdate(() => {}, false);
  }, [t, withUpdate]);

  // 支付失败回调
  const handlePaymentError = useCallback(
    (msg: 'timeout' | string) => {
      trackEvent(EventNewID.SUBSCRIPTION_POP_RESULT, {
        source: featureRef.current,
        plan: getPlanTag(activeComboRef.current, activeTabRef.current),
        result: StatusEnum.FAIL,
      });

      if (msg === 'timeout') {
        message.open({
          key: pollPayKey,
          type: 'error',
          content: t && t('merror.sub.longtime'),
          duration: 2,
        });
      } else {
        message.open({
          key: pollPayKey,
          type: 'error',
          content: msg || (t && t('merror.sub.purchasefail')),
          duration: 2,
        });
      }
    },
    [t],
  );

  // 主支付方法
  const handlePay = useCallback(
    async ({ data, btnIndex, feature, price, currency }: HandlePayProps) => {
      // 防止重复点击
      if (singleLoadingStates.some((state) => state)) return;

      setIsPurchaseSuccess(false);
      featureRef.current = feature;

      const plan = getPlanTag(activeComboRef.current, activeTabRef.current);

      // 埋点：开始结账
      trackGoogleEvent(GoogleEventID.BeginCheckout, {
        subscription_plan: plan,
      });
      trackEvent(EventNewID.SUBSCRIPTION_POP_CLICK, {
        source: feature,
        plan,
      });

      const commodityCode = data?.commodityCode as string;
      if (!commodityCode) {
        console.error('商品code不存在');
        return;
      }

      // 更新订单信息
      orderInfo.current = {
        orderId: '',
        orderType: OrderType.Once,
        orderCommodityCode: commodityCode,
      };

      // 获取商品的货币类型
      const commodityCurrency =
        (data?.content?.currency as CurrencyType) || selectedCurrency;

      try {
        setSingleBtnLoading(btnIndex, true);

        await executePayment(
          {
            feature,
            commodityCode,
            couponCode: '',
            currency: commodityCurrency,
            orderType: OrderType.Once,
          },
          {
            onSuccess: (orderId: string) => {
              orderInfo.current.orderId = orderId;
              message.open({
                key: pollPayKey,
                type: 'loading',
                content: t && t('merror.sub.purchasing'),
                duration: 0,
              });
            },
            onError: handlePaymentError,
          },
        );

        setSingleBtnLoading(btnIndex, false);
      } catch (error) {
        message.open({
          key: pollPayKey,
          type: 'error',
          content: (error as any).message || (t && t('merror.sub.purchasefail')),
          duration: 2,
        });
        setSingleBtnLoading(btnIndex, false);
      }
    },
    [
      singleLoadingStates,
      selectedCurrency,
      t,
      handlePaymentSuccess,
      handlePaymentError,
      setSingleBtnLoading,
    ],
  );

  // 监听页面可见性变化（用于 Stripe 支付回调）
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        if (!orderInfo.current.orderId || !orderInfo.current.orderType) {
          return;
        }

        try {
          await executePolling({
            orderInfo: orderInfo.current,
            t,
            pollPayKey,
            timer,
            callback: {
              onSuccess: handlePaymentSuccess,
              onError: handlePaymentError,
            },
          });
          await withUpdate(() => {}, false);
        } catch (error) {
          console.error(error);
        }
      } else {
        timer.current && clearTimeout(timer.current);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [paymentMethod, t, handlePaymentSuccess, handlePaymentError, withUpdate]);

  return {
    handlePay,
    singleLoadingStates,
    activeTab: 'once' as const,
    highlightSingleCode,
    formatSingleData,
    filteredSingleData,
    purchaseLimitLoading,
    isPurchaseSuccess,
    availableCurrencies,
  };
};

export default useAICreditPayment;
